import React from 'react';
import { useDispatch } from 'react-redux';
import { SensorData, DoorSensorData } from '../../types/SensorData';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { _fetchEnginList, _deleteMultipleReports } from '../../api/api';
import RapportList from './RapportList/RapportList';
import RapportDisplay from './RapportDisplay/RapportDisplay';
import RapportSettings from './RapportSettings/RapportSettings';
import RapportChose from './RapportChose/RapportChose';
import RapportExistingList from './RapportExistingList/RapportExistingList';
import NewRapportForm from './NewRapportForm/NewRapportForm';
import SensorReportGenerator from './SensorReportGenerator/SensorReportGenerator';

import ReportConfig from './ReportConfig/ReportConfig';
import { Report, ReportType } from './types/ReportTypes';
import {OlangItem} from '../../../shared/Olang/user-interface/OlangItem/OlangItem';
import { generateTagReport } from '../../slice/sensor.slice';
// @ts-ignore - PageHeader is a JSX component
import { PageHeader } from '../../../shared/PageHeader/PageHeader';

// Type pour les données de rapport du backend
interface BackendReport {
  uid: string
  id?: string | null
  title: string
  srcObject: string
  src?: string | null
  creaDate: string
  filepath?: string
  begDate?: string
  endDate?: string
}

export interface RapportProps {
  sensors?: (SensorData | DoorSensorData)[];
}

type ViewMode = 'main' | 'chose' | 'existing' | 'new-form' | 'sensor-detail' | 'settings' | 'sensor-report-generator' | 'report-config';

const Rapport: React.FC<RapportProps> = ({ sensors: propsSensors }) => {
  const dispatch = useDispatch();
  const toast = React.useRef<Toast>(null);
  
  // State pour les engins chargés depuis l'API
  const [sensors, setSensors] = React.useState<(SensorData | DoorSensorData)[]>([]);
  const [loadingSensors, setLoadingSensors] = React.useState(true);

  // Fonction pour charger les engins depuis l'API
  const loadEngins = async () => {
    try {
      setLoadingSensors(true);
      console.log('📊 Chargement des engins pour Rapport...');
      
      const response = await _fetchEnginList();
      console.log('📊 Réponse API engins:', response);
      
      if (response.data && response.data.success && response.data.result) {
        const enginsData = Array.isArray(response.data.result) ? response.data.result : [];
        console.log('📊 Engins data received:', enginsData.length);
        
        // Transformer les données engin en format SensorData/DoorSensorData
        const transformedSensors: (SensorData | DoorSensorData)[] = enginsData
          .filter((engin: any) => engin.temperature !== null || engin.humidity !== null)
          .map((engin: any) => {
            const baseData = {
              macAddr: engin.tagname || engin.macAddr || engin.uid || 'N/A',
              label: engin.title || engin.label || engin.reference || `Engin-${engin.uid}`,
              rssi: engin.rssi || 0,
              battery: parseInt(engin.batteries) || 0,
              alertDelay: 30,
              lastSeen: engin.lastSeenAt || engin.tagDate || new Date().toISOString(),
              alertes: false,
              rapports: true,
              actif: engin.sysActive === 1,
              historique: [],
              uid: engin.uid,
              id: engin.id,
              tagname: engin.tagname,
              title: engin.title,
              reference: engin.reference,
              name: engin.title || engin.label
            };

            // Si door est null, c'est un capteur normal
            if (engin.door === null) {
              return {
                ...baseData,
                temperature: engin.temperature || 0,
                humidity: engin.humidity || 0,
                temperatureMin: -5,
                temperatureMax: 25,
                humidityMin: 30,
                humidityMax: 70,
              } as SensorData;
            } else {
              // Si door est 0 ou 1, c'est un capteur avec données de porte
              return {
                ...baseData,
                temperature: engin.temperature || 0,
                humidity: engin.humidity || 0,
                ouvert: engin.door === 1,
                temperatureMin: -5,
                temperatureMax: 25,
                humidityMin: 30,
                humidityMax: 70,
              } as DoorSensorData;
            }
          });
        
        setSensors(transformedSensors);
        console.log('✅ Engins chargés pour Rapport:', transformedSensors.length);
      } else {
        console.log('❌ Aucun engin reçu');
        setSensors([]);
      }
      
    } catch (error) {
      console.error('🚨 Erreur lors du chargement des engins:', error);
      setSensors([]);
    } finally {
      setLoadingSensors(false);
    }
  };

  // Charger les engins au montage du composant
  React.useEffect(() => {
    // Utiliser les capteurs passés en props ou charger depuis l'API
    if (propsSensors && propsSensors.length > 0) {
      setSensors(propsSensors);
      setLoadingSensors(false);
    } else {
      loadEngins();
    }
  }, [propsSensors]);

  // État pour gérer l'affichage des composants
  const [viewMode, setViewMode] = React.useState<ViewMode>('main');
  const [selectedReportType, setSelectedReportType] = React.useState<ReportType | null>(null);
  const [selectedReport, setSelectedReport] = React.useState<Report | null>(null);
  const [selectedMac, setSelectedMac] = React.useState<string>(sensors[0]?.macAddr || '');
  
  const selectedSensor = sensors.find(s => s.macAddr === selectedMac) || null;

  // Fonction pour gérer la génération de rapports
  const handleGenerateReport = async (reportPayload: any) => {
    try {
      console.log(' Génération du rapport avec payload:', reportPayload);
      // @ts-ignore - Temporary fix for TypeScript dispatch issue
      const result = await dispatch(generateTagReport(reportPayload));
      console.log(' Rapport généré avec succès:', result);
      // Optionnel : rediriger vers la liste des rapports après génération
      // setViewMode('existing');
    } catch (error) {
      console.error('❌ Erreur lors de la génération du rapport:', error);
    }
  };

  const handleSensorSelect = (macAddr: string) => {
    setSelectedMac(macAddr);
    setViewMode('sensor-detail');
  };

  const handleSelectReportType = (reportType: ReportType) => {
    setSelectedReportType(reportType);
    setViewMode('new-form');
  };

  const handleViewReport = (report: BackendReport) => {
    setSelectedReport(report as any); // Conversion temporaire pour compatibilité
    // Logique pour afficher le rapport existant
    console.log('Affichage du rapport:', report.title);
  };

  const handleDeleteReport = (reportId: string) => {
    confirmDialog({
      message: 'Voulez-vous vraiment supprimer ce rapport ?',
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      accept: async () => {
        try {
          console.log('🗑️ Suppression du rapport:', reportId);
          const response = await _deleteMultipleReports(reportId);
          console.log('🗑️ Réponse suppression:', response);
          
          if (response.data && !response.data.error) {
            toast.current?.show({
              severity: 'success',
              summary: 'Succès',
              detail: 'Rapport supprimé avec succès',
              life: 3000
            });
            // Forcer le rechargement de la liste dans RapportExistingList
            // En changeant temporairement de vue puis en revenant
            setViewMode('main');
            setTimeout(() => setViewMode('existing'), 100);
          } else {
            toast.current?.show({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Erreur lors de la suppression du rapport',
              life: 3000
            });
          }
        } catch (error: any) {
          console.error('🗑️ Erreur suppression:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Erreur',
            detail: error.message || 'Erreur lors de la suppression',
            life: 3000
          });
        }
      }
    });
  };

  const handleSubmitNewReport = (formData: any) => {
    // Logique pour créer un nouveau rapport
    console.log('Création du nouveau rapport:', formData);
    setViewMode('main');
  };

  const handleExportCSV = () => {
    // Logique d'export CSV
    console.log('Export CSV pour le capteur:', selectedSensor?.macAddr);
  };

  const renderMainMenu = () => (
    <div className="min-h-screen surface-ground p-6">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        {/* @ts-ignore */}
        <PageHeader
          titleOlang="reports.center"
          backPath="/Capteurs/index"
          info={
            <div className="flex align-items-center gap-2">
              <i className="pi pi-file text-xl text-primary"></i>
              <span className="text-lg font-semibold"><OlangItem olang='reports.management' /></span>
            </div>
          }
          className="mb-4"
        />

        {/* Cards avec PrimeFlex - Seulement 2 options */}
        <div className="grid">
          <div className="col-12 md:col-6 lg:col-6">
            <Card 
              className="cursor-pointer hover:shadow-4 transition-all transition-duration-300 h-full"
              onClick={() => setViewMode('report-config')}
            >
              <div className="text-center p-6">
                <div className="mb-4">
                  <i className="pi pi-file-plus text-6xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-bold text-900 mb-3">
                  Nouveau Rapport
                </h3>
                <p className="text-600 text-lg mb-4">
                  Créez un nouveau rapport personnalisé avec vos capteurs sélectionnés
                </p>
                <Button 
                  label="Créer un rapport" 
                  icon="pi pi-plus" 
                  className="p-button-primary p-button-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMode('report-config');
                  }}
                />
              </div>
            </Card>
          </div>

          <div className="col-12 md:col-6 lg:col-6">
            <Card 
              className="cursor-pointer hover:shadow-4 transition-all transition-duration-300 h-full"
              onClick={() => setViewMode('existing')}
            >
              <div className="text-center p-6">
                <div className="mb-4">
                  <i className="pi pi-folder-open text-6xl text-indigo-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-900 mb-3">
                  Liste des Rapports
                </h3>
                <p className="text-600 text-lg mb-4">
                  Consultez, téléchargez et gérez vos rapports existants
                </p>
                <Button 
                  label="Voir les rapports" 
                  icon="pi pi-list" 
                  className="p-button-secondary p-button-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMode('existing');
                  }}
                />
              </div>
            </Card>
          </div>
        </div>

        {/* Section d'information */}
        <div className="mt-6">
          <Card className="bg-primary-50 border-primary">
            <div className="flex align-items-center gap-3 p-4">
              <i className="pi pi-info-circle text-primary text-2xl"></i>
              <div>
                <h4 className="text-primary font-semibold mb-1">Information</h4>
                <p className="text-primary-700 m-0">
                  Utilisez "Nouveau Rapport" pour générer des analyses personnalisées de vos capteurs, 
                  ou "Liste des Rapports" pour accéder à vos rapports précédemment générés.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );

  const renderSensorDetail = () => (
    <div className="flex h-full">
      <RapportList 
        selectedMac={selectedMac}
        onSensorSelect={handleSensorSelect}
        onToggleSettings={() => setViewMode('settings')}
      />
      <RapportDisplay 
        selectedSensor={selectedSensor}
        onExportCSV={handleExportCSV}
      />
    </div>
  );

  return (
    <div className="h-full">
      <Toast ref={toast} />
      <ConfirmDialog />
      {viewMode === 'main' && renderMainMenu()}
      
      {viewMode === 'chose' && (
        <RapportChose 
          onSelectReportType={handleSelectReportType}
          onBack={() => setViewMode('main')}
        />
      )}
      
      {viewMode === 'existing' && (
        <RapportExistingList 
          onBack={() => setViewMode('main')}
          onViewReport={handleViewReport}
          onDeleteReport={handleDeleteReport}
        />
      )}
      
      {viewMode === 'new-form' && selectedReportType && (
        <NewRapportForm 
          reportType={selectedReportType}
          sensors={sensors}
          onBack={() => setViewMode('chose')}
          onSubmit={handleSubmitNewReport}
        />
      )}
      
      {viewMode === 'sensor-detail' && renderSensorDetail()}
      
      {viewMode === 'sensor-report-generator' && (
        <SensorReportGenerator 
          onBack={() => setViewMode('main')}
        />
      )}
      
      {viewMode === 'settings' && (
        <RapportSettings 
          onClose={() => setViewMode('sensor-detail')}
        />
      )}
      
      {viewMode === 'report-config' && (
        <ReportConfig 
          onBack={() => setViewMode('main')}
          onGenerate={handleGenerateReport}
        />
      )}
      

    </div>
  );
};

export default Rapport;
